import React, { useEffect, useState } from "react";
import { Loader2, Check, X, Zap, MessageSquare, Sliders, ChevronDown, ChevronUp, ExternalLink, Mail, LayoutDashboard, Users, LineChart, AlertTriangle, Copy, Compass, Layers } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import SignalBadge from "../components/SignalBadge";
import { NeutralMarketTag } from "../components/NeutralMarketTag";
import TierBadge from "../components/TierBadge";
import AlertClassBadge from "../components/AlertClassBadge";
import AlertStatusBadge from "../components/AlertStatusBadge";
import MoonbucksScoreCard from "../components/MoonbucksScoreCard";
import ScannerTuningCard from "../components/ScannerTuningCard";
import AlertFilterPanel from "../components/AlertFilterPanel";
import PulseStrip from "../components/PulseStrip";
import ThesisEditor, { pickSections } from "../components/ThesisEditor";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function AdminReview() {
  const [pending, setPending] = useState([]);
  const [filteredPending, setFilteredPending] = useState([]);
  const [allAlerts, setAllAlerts] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        api.get("/alerts/admin/pending"),
        api.get("/alerts/admin/all"),
        api.get("/admin/users"),
      ]);
      setPending(r1.data); setAllAlerts(r2.data); setUsers(r3.data);
    } catch (e) {
      console.error("admin load failed", e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const review = async (alertId, action, note = null, thesis = null) => {
    try {
      await api.post("/alerts/admin/review", { alert_id: alertId, action, edit_note: note, thesis });
      toast.success(`Alert ${action === "approve" ? "approved → broadcast" : "rejected"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      let job = (await api.post("/alerts/admin/generate")).data;
      const until = Date.now() + 6 * 60 * 1000;
      while (job.status === "running" && Date.now() < until) {
        await new Promise((res) => setTimeout(res, 3000));
        try {
          job = (await api.get("/alerts/admin/generate/status")).data;
        } catch (pollErr) {
          // transient network blip — keep polling
        }
      }
      if (job.status !== "done") throw new Error(job.error || "Scan is still running — check back in a minute");
      const d = job.result;
      const parts = [
        `Queued ${d.queued} new`,
        d.refreshed ? `refreshed ${d.refreshed}` : null,
        d.faded ? `auto-rejected ${d.faded} faded` : null,
      ].filter(Boolean);
      toast.success(`${parts.join(" · ")} (${d.candidates_found} candidates on this scan)`);
      load();
    } catch (e) {
      toast.error(`Generation failed: ${e?.response?.data?.detail || e.message}`);
    }
    setGenerating(false);
  };

  const toggleSub = async (userId, currentSub) => {
    try {
      await api.post("/admin/users/toggle-subscription", { user_id: userId, subscribed: !currentSub });
      toast.success("Updated");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const [pingingDiscord, setPingingDiscord] = useState(false);
  const [dedupingQueue, setDedupingQueue] = useState(false);
  const dedupeQueue = async () => {
    setDedupingQueue(true);
    try {
      const r = await api.post("/admin/pending/dedupe");
      const d = r.data || {};
      if (d.total_dropped > 0) {
        const parts = [];
        if (d.dropped_pending_dupes) parts.push(`${d.dropped_pending_dupes} superseded pending`);
        if (d.dropped_open_dupes) parts.push(`${d.dropped_open_dupes} covered by open position`);
        toast.success(`Swept ${d.total_dropped} legacy dupe${d.total_dropped === 1 ? "" : "s"} — ${parts.join(" · ")}`);
        load();
      } else {
        toast.success(`Queue clean — scanned ${d.scanned}, nothing to sweep`);
      }
    } catch (e) {
      toast.error("Dedupe sweep failed");
    }
    setDedupingQueue(false);
  };

  const testDiscord = async () => {
    setPingingDiscord(true);
    try {
      const r = await api.post("/admin/discord/test");
      const { configured = [], results = {}, any_ok } = r.data || {};
      const okNames = configured.filter((n) => results[n]?.ok);
      const failNames = configured.filter((n) => !results[n]?.ok);
      if (any_ok && failNames.length === 0) {
        toast.success(`Discord ✓ — ${okNames.join(", ")}`);
      } else if (any_ok) {
        toast.success(`Discord: ${okNames.join(", ")} ✓ · stale: ${failNames.join(", ")}`);
      } else {
        toast.error(`All Discord webhooks failed: ${failNames.join(", ")}`);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Discord ping failed";
      toast.error(msg);
    }
    setPingingDiscord(false);
  };

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-3 mb-8">
          <div>
            <div className="label-uppercase text-mb-emerald">Analyst desk · Review queue</div>
            <h1 className="font-display font-semibold text-4xl tracking-tight mt-1" data-testid="admin-title">Vetting cockpit</h1>
            <p className="text-neutral-600 mt-1 text-sm">Sign off on alerts before they ship to subscribers.</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2 max-w-full">
            <Link to="/admin/dashboard">
              <Button variant="outline" className="rounded-md" data-testid="admin-ops-link">
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Ops
              </Button>
            </Link>
            <Link to="/admin/users">
              <Button variant="outline" className="rounded-md" data-testid="admin-users-link">
                <Users className="w-4 h-4 mr-2" />
                Users
              </Button>
            </Link>
            <Link to="/admin/backtest" data-testid="admin-backtest-link">
              <Button variant="outline" className="rounded-md">
                <Sliders className="w-4 h-4 mr-2" />
                Backtest editor
              </Button>
            </Link>
            <Link to="/admin/portfolio" data-testid="admin-portfolio-link">
              <Button variant="outline" className="rounded-md">
                <LineChart className="w-4 h-4 mr-2" />
                Portfolio console
              </Button>
            </Link>
            <Link to="/admin/boosters" data-testid="admin-boosters-link">
              <Button variant="outline" className="rounded-md">
                <Zap className="w-4 h-4 mr-2" />
                Boosters
              </Button>
            </Link>
            <Link to="/admin/screener-blueprint" data-testid="admin-blueprint-link">
              <Button variant="outline" className="rounded-md" title="One-page introspection: what's in our universe, what we screen on, coverage gaps">
                <Compass className="w-4 h-4 mr-2" />
                Screener blueprint
              </Button>
            </Link>
            <Link to="/admin/backtests/universe-expansion" data-testid="admin-uxbt-link">
              <Button variant="outline" className="rounded-md" title="12-month replay: today's scan list vs every stock over $500M">
                <Layers className="w-4 h-4 mr-2" />
                $500M+ backtest
              </Button>
            </Link>
            <Link to="/admin/email">
              <Button variant="outline" className="rounded-md" data-testid="admin-email-link">
                <Mail className="w-4 h-4 mr-2" />
                Email console
              </Button>
            </Link>
            <Button onClick={testDiscord} disabled={pingingDiscord} variant="outline" className="rounded-md" data-testid="admin-discord-test-btn">
              {pingingDiscord ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageSquare className="w-4 h-4 mr-2" />}
              Ping Discord
            </Button>
            <Button onClick={dedupeQueue} disabled={dedupingQueue} variant="outline" className="rounded-md" data-testid="admin-dedupe-btn" title="Sweep legacy duplicate pending alerts">
              {dedupingQueue ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />}
              Sweep dupes
            </Button>
            <Button onClick={generate} disabled={generating} className="bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-md" data-testid="admin-generate-btn">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Generate fresh alerts
            </Button>
          </div>
        </div>

        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="bg-white border border-mb-border p-1">
            <TabsTrigger value="pending" data-testid="admin-tab-pending" className="data-[state=active]:bg-mb-emerald data-[state=active]:text-white rounded-md">
              Pending ({pending.length})
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="admin-tab-history">
              All ({allAlerts.length})
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">
              Users ({users.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-6">
            <PulseStrip />
            {/* Live-tunable scanner filters — cap, confidence gate, moonshot grade.
                Changes hit the next scanner tick (30s server cache). */}
            <div className="mb-5">
              <ScannerTuningCard />
            </div>
            {/* Client-side "find the best among them" filter panel — narrows
                the pending queue by conviction drivers we've seen produce
                the biggest moves (earnings-in-N-days, insider buying,
                news score, squeeze setup, screen signals fired). */}
            {pending.length > 0 && (
              <AlertFilterPanel alerts={pending} onFilteredChange={setFilteredPending} />
            )}
            {loading ? <Spinner /> : (
              pending.length === 0
                ? <Empty msg="No pending alerts. Generate fresh ones above or wait for the next 15-min scan." />
                : filteredPending.length === 0
                  ? <Empty msg="No alerts match the current filters — clear a filter to widen the queue." />
                  : <div className="space-y-4" data-testid="pending-list">{filteredPending.map((a) => <PendingCard key={a.id} alert={a} onReview={review} />)}</div>
            )}
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            {loading ? <Spinner /> : (
              <div className="bg-white border border-mb-border rounded-lg overflow-hidden" data-testid="history-list">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-mb-border">
                    <tr><Th>Time</Th><Th>Symbol</Th><Th>Class</Th><Th>Action</Th><Th>Review</Th><Th>Outcome</Th><Th>Return</Th><Th>Conf.</Th></tr>
                  </thead>
                  <tbody>
                    {allAlerts.map((a) => {
                      const live = a.live || null;
                      const ret = live?.return_pct;
                      return (
                      <tr key={a.id} className="border-b border-neutral-100" data-testid={`history-row-${a.symbol}`}>
                        <Td className="font-mono-data text-neutral-500 text-xs">{new Date(a.created_at).toLocaleString()}</Td>
                        <Td className="font-mono-data font-bold">{a.symbol}</Td>
                        <Td><AlertClassBadge alertClass={a.alert_class || "steady"} compact /><NeutralMarketTag regime={a.regime} testId={`neutral-market-tag-${a.symbol}`} /></Td>
                        <Td><SignalBadge action={a.action} confidence={a.confidence} size="sm" /></Td>
                        <Td>
                          <span className={`label-uppercase ${a.status === "approved" ? "text-mb-emerald" : a.status === "rejected" ? "text-sell" : "text-neutral-500"}`}>
                            {a.status.replace("_", " ")}
                          </span>
                        </Td>
                        <Td>
                          {live ? (
                            <div className="flex items-center gap-1.5">
                              <AlertStatusBadge live={live} alertClass={a.alert_class} compact />
                              {live.status === "ACTIVE" && live.days_remaining != null && (
                                <span
                                  className={`font-mono-data text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded ${live.days_remaining <= 2 ? "bg-amber-100 text-amber-800" : "bg-neutral-100 text-neutral-600"}`}
                                  title={`Day ${live.days_elapsed} of ${live.max_hold_days}`}
                                >
                                  {live.days_remaining}d
                                </span>
                              )}
                            </div>
                          ) : <span className="text-neutral-300 text-xs">—</span>}
                        </Td>
                        <Td>
                          {ret != null ? (
                            <span className={`font-mono-data font-bold ${ret >= 0 ? "text-emerald-700" : "text-red-700"}`}>
                              {ret >= 0 ? "+" : ""}{ret}%
                            </span>
                          ) : <span className="text-neutral-300 text-xs">—</span>}
                        </Td>
                        <Td className="font-mono-data">{Math.round(a.confidence * 100)}%</Td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="users" className="mt-6">
            {loading ? <Spinner /> : (
              <div className="bg-white border border-mb-border rounded-lg overflow-hidden" data-testid="users-list">
                <table className="w-full text-sm">
                  <thead className="bg-neutral-50 border-b border-mb-border">
                    <tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>Subscribed</Th><Th>Action</Th></tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-neutral-100" data-testid={`user-row-${u.email}`}>
                        <Td>{u.name}</Td>
                        <Td className="font-mono-data text-xs">{u.email}</Td>
                        <Td className="label-uppercase text-mb-emerald">{u.role}</Td>
                        <Td>{u.subscribed ? "✓" : "—"}</Td>
                        <Td>
                          {u.role !== "admin" && (
                            <Button size="sm" variant="outline" className="rounded-md" onClick={() => toggleSub(u.id, u.subscribed)} data-testid={`toggle-sub-${u.email}`}>
                              {u.subscribed ? "Cancel sub" : "Activate sub"}
                            </Button>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function PendingCard({ alert, onReview }) {
  const [note, setNote] = useState("");
  const [thesis, setThesis] = useState(alert.thesis_draft ? pickSections(alert.thesis_draft) : null);
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const cls = alert.alert_class || "steady";
  const isMoon = cls === "moonshot";

  // Divergence flag: trigger confidence (0-1) vs. an INDEPENDENT backdrop
  // score derived from conviction stack + sentiment + regime + grade
  // (deliberately excludes confidence itself so the two numbers can
  // actually disagree). A >30 pt gap means the pattern is one thing, the
  // surrounding fundamentals / macro are telling a different story.
  const confidencePct = Math.round((alert.confidence || 0) * 100);
  const backdropVal = alert.backdrop_score != null ? Math.round(alert.backdrop_score) : null;
  const divergence = backdropVal != null ? confidencePct - backdropVal : 0;
  const showDivergence = backdropVal != null && Math.abs(divergence) > 30;
  const divergenceLabel = divergence > 0
    ? "Strong pattern, weak macro/catalyst backdrop"
    : "Soft trigger, unusually strong backdrop";

  const toggleDetail = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !detail) {
      setLoadingDetail(true);
      try {
        const r = await api.get(`/stocks/${alert.symbol}`);
        setDetail(r.data);
      } catch (e) {
        toast.error(`Couldn't load ${alert.symbol} detail`);
      }
      setLoadingDetail(false);
    }
  };

  return (
    <div className={`bg-white border-2 rounded-lg p-6 ${isMoon ? "border-pink-300" : "border-mb-border"}`} data-testid={`pending-${alert.symbol}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono-data font-extrabold text-2xl">{alert.symbol}</span>
          <AlertClassBadge alertClass={cls} compact />
          <NeutralMarketTag regime={alert.regime} testId={`pending-neutral-tag-${alert.symbol}`} />
          <SignalBadge action={alert.action} confidence={alert.confidence} />
          {alert.tier && <TierBadge tier={alert.tier} upside={alert.upside} />}
        </div>
        <span className="font-mono-data text-xs text-neutral-400">{new Date(alert.created_at).toLocaleString()}</span>
      </div>
      <p className="mt-3 text-sm text-neutral-700 leading-relaxed">{alert.reason}</p>

      {showDivergence && (
        <div
          className="mt-3 flex items-start gap-2 rounded-md px-3 py-2 border border-amber-300 bg-amber-50"
          data-testid={`divergence-warning-${alert.symbol}`}
          title={`Confidence ${confidencePct}% · Backdrop ${backdropVal}/100 — ${Math.abs(divergence)} pt gap`}
        >
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-900 leading-snug">
            <span className="font-bold uppercase tracking-wider text-[10px] text-amber-700">
              Score divergence
            </span>
            <span className="mx-1.5 text-amber-500">·</span>
            <span className="font-semibold">{divergenceLabel}.</span>{" "}
            <span className="font-mono-data text-amber-700">
              Conf {confidencePct}% vs Backdrop {backdropVal}
              {" · "}{Math.abs(divergence)}pt gap
            </span>
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2 text-center text-xs font-mono-data">
        <KV label="Price" val={`$${alert.indicators.last_price}`} />
        <KV label="RSI" val={alert.indicators.rsi} />
        <KV label="Vol" val={`${alert.indicators.volume_ratio}×`} />
        <KV label="Signals" val={`${alert.screen.score}/${alert.screen.max_score || 6}`} tooltip="Number of screen components firing (breakout, volume, RSI, etc.). The Moonbucks Score below rolls this in with macro and catalyst-risk factors." />
        <KV label="Regime" val={alert.regime.regime} />
      </div>

      {/* Vet in place — expand to see the full Moonbucks Score + pillar breakdown
          + blockers without leaving the review queue. */}
      <div className="mt-4 flex items-center gap-2">
        <button
          type="button"
          onClick={toggleDetail}
          className="text-sm font-semibold text-mb-emerald hover:underline inline-flex items-center gap-1"
          data-testid={`review-toggle-${alert.symbol}`}
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {expanded ? "Hide review detail" : "Review in detail"}
        </button>
        <Link
          to={`/app/stock/${alert.symbol}`}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-neutral-500 hover:text-mb-emerald inline-flex items-center gap-1"
          data-testid={`open-stock-${alert.symbol}`}
        >
          Open full page <ExternalLink className="w-3 h-3" />
        </Link>
      </div>

      {expanded && (
        <div
          className="mt-4 rounded-lg overflow-hidden"
          style={{ background: "#0f0c1d" }}
          data-testid={`review-detail-${alert.symbol}`}
        >
          {loadingDetail && (
            <div className="p-8 text-center text-white/60 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading full score…
            </div>
          )}
          {detail && (
            <div className="p-4 space-y-4">
              <MoonbucksScoreCard grade={detail.moonbucks_grade} />
              {detail.blockers?.length > 0 && (
                <div className="rounded-md p-4"
                     style={{ background: "rgba(245, 166, 35, 0.08)", border: "1px solid rgba(245, 166, 35, 0.35)" }}
                     data-testid={`blockers-${alert.symbol}`}>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-300 mb-2">
                    Why the desk might pause
                  </div>
                  <ul className="space-y-1.5 text-sm text-white/85">
                    {detail.blockers.map((b, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          b.severity === "high" ? "bg-red-400" :
                          b.severity === "medium" ? "bg-amber-400" : "bg-white/60"
                        }`} />
                        <div>
                          <span className="font-semibold text-white">{b.label}</span>
                          <span className="text-white/60"> — {b.hint}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {detail.conviction_stack && !detail.conviction_stack.error && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 text-center text-xs font-mono-data" data-testid={`stack-summary-${alert.symbol}`}>
                  {(detail.conviction_stack.signals || []).map((s, i) => {
                    const fired = s.fired || (s.score >= 2);
                    return (
                      <div
                        key={i}
                        className="p-3 rounded-md"
                        style={{
                          background: fired ? "rgba(94, 234, 212, 0.12)" : "rgba(255,255,255,0.04)",
                          border: fired ? "1px solid rgba(94, 234, 212, 0.35)" : "1px solid rgba(255,255,255,0.08)",
                        }}
                        title={s.headline || ""}
                        data-testid={`stack-signal-${s.key || s.label}`}
                      >
                        <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">{s.label || s.key}</div>
                        <div className={`font-bold mt-1 ${fired ? "text-mb-emerald" : "text-white"}`}>{s.score}/3</div>
                        {s.headline && (
                          <div className="mt-1 text-[10px] text-white/60 leading-tight line-clamp-2">{s.headline}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <ThesisEditor alert={alert} value={thesis} onChange={setThesis} />

      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <Input
          placeholder="Optional desk note (shown to subscribers on the alert)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="flex-1"
          data-testid={`note-input-${alert.symbol}`}
        />
        <Button
          onClick={() => onReview(alert.id, "approve", note || null, thesis)}
          className="bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-md"
          data-testid={`approve-${alert.symbol}`}
        >
          <Check className="w-4 h-4 mr-1" /> Approve & Broadcast
        </Button>
        <Button
          variant="outline"
          onClick={() => onReview(alert.id, "reject")}
          className="rounded-md"
          data-testid={`reject-${alert.symbol}`}
        >
          <X className="w-4 h-4 mr-1" /> Reject
        </Button>
      </div>
    </div>
  );
}

const KV = ({ label, val, tooltip }) => (
  <div
    className={`rounded-md bg-neutral-100 p-2 ${tooltip ? "cursor-help" : ""}`}
    title={tooltip || undefined}
  >
    <div className="text-neutral-500 label-uppercase" style={{ letterSpacing: "0.1em" }}>{label}{tooltip && <span className="ml-0.5 text-neutral-400">·ⓘ</span>}</div>
    <div className="mt-0.5 font-bold text-neutral-950">{val}</div>
  </div>
);
const Th = ({ children }) => <th className="px-6 py-3 text-left label-uppercase text-neutral-500">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-6 py-3 ${className}`}>{children}</td>;
const Spinner = () => <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-mb-emerald" /></div>;
const Empty = ({ msg }) => <div className="py-16 text-center text-neutral-500 bg-white rounded-lg border border-mb-border">{msg}</div>;
